import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  BucketBackfillFailedIssue,
  ClientErrorIssue,
  DetailedIssue,
  DrsAgentNotFoundIssue,
  DynamoDbBackupModeNotConfiguredIssue,
  EksClusterAccessDeniedIssue,
  EksRequiredEndpointInaccessibleIssue,
  FailbackResourceIssue,
  KeyPairNotFoundIssue,
  MalwareScanIssue,
  MarketplaceActionRequiredIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MultipleMatchingImagesIssue,
  MarketplaceInstanceCantBeCopiedIssue,
  NewTargetPlanRequiredIssue,
  PersistentIssue,
  QuotaIssue,
  ResourcePolicyDeniedIssue,
  S3BucketReplicationNotConfiguredIssue,
  S3RecoveryBucketNameConflictIssue,
  ServiceControlPolicyPermissionIssue,
  SubscriptionOverageIssue,
  SyncPairPermissionIssue,
  UnconfiguredS3BucketReplicationIssue,
  UninstallDrsAgentIssue,
  UnconfiguredAzureBlobContainerIssue,
  RefreshClientErrorIssue,
} from 'src/app/account/services/issue'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import { Application } from '../../../services/application.service'
import { AccountService } from 'src/app/account/services/account.service'

@Component({
  selector: 'issue-body',
  templateUrl: './issue-body.component.html',
  styleUrls: ['./issue-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IssueBodyComponent implements OnInit, OnChanges {
  // Issue type classes exposed to the template for instanceof checks (Angular templates can't import directly)
  AcmCertificateNotFoundIssue = AcmCertificateNotFoundIssue
  AmiStorageNotAccessibleIssue = AmiStorageNotAccessibleIssue
  BucketBackfillFailedIssue = BucketBackfillFailedIssue
  S3RecoveryBucketNameConflictIssue = S3RecoveryBucketNameConflictIssue
  ClientErrorIssue = ClientErrorIssue
  DetailedIssue = DetailedIssue
  DrsAgentNotFoundIssue = DrsAgentNotFoundIssue
  DynamoDbBackupModeNotConfiguredIssue = DynamoDbBackupModeNotConfiguredIssue
  EksClusterAccessDeniedIssue = EksClusterAccessDeniedIssue
  EksRequiredEndpointInaccessibleIssue = EksRequiredEndpointInaccessibleIssue
  FailbackResourceIssue = FailbackResourceIssue
  KeyPairNotFoundIssue = KeyPairNotFoundIssue
  MalwareScanIssue = MalwareScanIssue
  MarketplaceActionRequiredIssue = MarketplaceActionRequiredIssue
  MarketplaceAmiCantBeCopiedIssue = MarketplaceAmiCantBeCopiedIssue
  MultipleMatchingImagesIssue = MultipleMatchingImagesIssue
  MarketplaceInstanceCantBeCopiedIssue = MarketplaceInstanceCantBeCopiedIssue
  PersistentIssue = PersistentIssue
  QuotaIssue = QuotaIssue
  ResourcePolicyDeniedIssue = ResourcePolicyDeniedIssue
  RefreshClientErrorIssue = RefreshClientErrorIssue
  S3BucketReplicationNotConfiguredIssue = S3BucketReplicationNotConfiguredIssue
  ServiceControlPolicyDeniedIssue = ServiceControlPolicyPermissionIssue
  SubscriptionOverageIssue = SubscriptionOverageIssue
  SyncPairPermissionIssue = SyncPairPermissionIssue
  UnconfiguredS3BucketReplicationIssue = UnconfiguredS3BucketReplicationIssue
  UnconfiguredAzureBlobContainerIssue = UnconfiguredAzureBlobContainerIssue
  UninstallDrsAgentIssue = UninstallDrsAgentIssue
  NewTargetPlanRequiredIssue = NewTargetPlanRequiredIssue

  @Input() issue: ViewableIssue = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null

  // Cached issue type — computed once in ngOnInit instead of on every CD cycle
  cachedIssueType: string = null

  constructor(private accountService: AccountService) {}

  ngOnInit(): void {
    this.cachedIssueType = this.computeIssueType()
  }

  // Re-compute cached type when the issue input changes (e.g. switching selected resource)
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['issue'] && !changes['issue'].firstChange) {
      this.cachedIssueType = this.computeIssueType()
    }
  }

  // Show IAM-specific display name only when the account has manual IAM resource entitlement
  private computeIssueType(): string {
    if (
      this.issue.issue instanceof SyncPairPermissionIssue &&
      this.issue.issue.isInsufficientIamPermissionIssue &&
      this.accountService.entitledForCapability('iamManualResources')
    ) {
      return SyncPairPermissionIssue.insufficientIamPermissionIssueDisplayName
    }
    return this.issue.issue.issueType
  }
}
